<template>
  <ProductDetailGallery
    ref="galleryRoot"
    :items="galleryItems"
    :lightbox-gallery="lightboxGallery"
    :is-loading="isGalleryLoading"
  >
    <BvsSimulationScenePreviews
      :manifest="bvsManifest"
      :is-loading="bvsLoading"
      @select-scene="emit('select-bvs-scene', $event)"
    />
  </ProductDetailGallery>
</template>
<script setup lang="ts">
import type {
  GalleryItem,
  LightboxGalleryInterface,
  MediaItemInterface
} from '@hypercodestudio/basler-components';

import ProductDetailGallery from '~/components/shop/ProductDetailGallery.vue';
import BvsSimulationScenePreviews from '~/components/shop/bvs/BvsSimulationScenePreviews.vue';
import { applyPdpGallerySlotSizes } from '~/utils/shop/applyPdpGallerySlotSizes';
import { getProductImage } from '~/utils/shop/getProductImage';
import type { IMedia } from '~/utils/guards/isMediaType';

import { useMedia } from '~/composables/useMedia';
import { isDefined } from '~/utils/guards/isDefined';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import type { IProductEnrichment } from '~~/lib/ContentfulService';
import type { BvsCameraManifest } from '~/types/bvs';
import { useProgressiveMediaOverlay } from '~/composables/useProgressiveMediaOverlay';
import { generateImageObject } from '~/utils/generateImageObject';
import {
  PDP_GALLERY_SECONDARY_THUMB_HEIGHT,
  PDP_GALLERY_SECONDARY_THUMB_WIDTH,
  pdpGalleryLightboxImageOptions,
  pdpGallerySecondaryThumbnailImageOptions,
  pdpGalleryThumbnailImageOptions
} from '~/utils/imagePresetRegistry';

const store = useProductDetailPageStore();
const { $textDictionary, $globalPageSettings } = useNuxtApp();

const galleryTitle = useHydrationSafeDictionaryText(
  'media.gallery.title',
  'Gallery of all media elements'
);
const galleryTriggerLabel = useHydrationSafeDictionaryText(
  'media.gallery.trigger.btn.label',
  'Open media in lightbox'
);
const galleryNextLabel = useHydrationSafeDictionaryText(
  'media.gallery.next.btn.label',
  'Next media'
);
const galleryPrevLabel = useHydrationSafeDictionaryText(
  'media.gallery.prev.btn.label',
  'Previous media'
);
const galleryCloseLabel = useHydrationSafeDictionaryText(
  'media.gallery.close.btn.label',
  'Close lightbox'
);

const fallbackImage =
  $globalPageSettings.value?.shopConfig?.fallbackPosterImage;
const fallbackChartImage =
  $globalPageSettings.value?.shopConfig?.fallbackChartImage;

const PDP_PREVIEW_WIDTH = 160;
const PDP_PREVIEW_QUALITY = 40;
const PDP_FIXED_THUMB_QUALITY = 80;

/** Keep skeleton visible until product data exists to avoid an empty gallery gap. */
const isGalleryLoading = computed(
  () => store.loading || store.decoratedProduct === null
);

interface Props {
  productEnrichments?: IProductEnrichment[];
  bvsManifest?: BvsCameraManifest;
  bvsLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productEnrichments: () => [],
  bvsManifest: undefined,
  bvsLoading: false
});

interface Emits {
  (event: 'select-bvs-scene', sceneId: string): void;
}

const emit = defineEmits<Emits>();

const galleryRoot = ref<InstanceType<typeof ProductDetailGallery>>();

const buildFixedThumbnailMedia = (
  imageUrl: string | undefined
): MediaItemInterface => {
  const media = generateImageObject(imageUrl ? { url: imageUrl } : undefined, {
    ...pdpGallerySecondaryThumbnailImageOptions,
    quality: PDP_FIXED_THUMB_QUALITY,
    keepAspectRatio: true
  });

  if (media) {
    return media;
  }

  return {
    type: 'image',
    content: {
      height: PDP_GALLERY_SECONDARY_THUMB_HEIGHT,
      width: PDP_GALLERY_SECONDARY_THUMB_WIDTH,
      src: ''
    }
  };
};

const productImagesThumbnails = computed<GalleryItem[]>(() => {
  if (!store.decoratedProduct) {
    return [];
  }

  return getProductImage(
    store.decoratedProduct.product,
    (imageIndex) =>
      imageIndex === 0
        ? pdpGalleryThumbnailImageOptions
        : pdpGallerySecondaryThumbnailImageOptions,
    $globalPageSettings.value?.shopConfig?.defaultProductImage ?? undefined
  );
});

const productImages = computed<GalleryItem[]>(() => {
  if (!store.decoratedProduct) {
    return [];
  }

  return getProductImage(
    store.decoratedProduct.product,
    pdpGalleryLightboxImageOptions,
    $globalPageSettings.value?.shopConfig?.defaultProductImage ?? undefined
  );
});

const productEnrichmentGallery = computed<IMedia[]>(() => {
  const items: IMedia[] = [];
  props.productEnrichments.forEach((element) => {
    if (element?.fields?.contentSlotGallery) {
      element.fields.contentSlotGallery.forEach((item) => {
        items.push(item);
      });
    }
  });
  return items;
});

const productEnrichmentGalleryThumbnails = computed<GalleryItem[]>(() => {
  const items: GalleryItem[] = [];

  props.productEnrichments.forEach((element) => {
    if (element?.fields?.contentSlotGallery) {
      element.fields.contentSlotGallery.forEach((item) => {
        items.push({
          media: buildFixedThumbnailMedia(
            item.fields?.posterImage?.fields?.file?.url ??
              fallbackImage?.url ??
              undefined
          )
        });
      });
    }
  });
  return items;
});

const media = ref<Record<string, MediaItemInterface>>({});

// Load media items asynchronously; onCleanup prevents stale writes when enrichments change.
watchEffect((onCleanup) => {
  let cancelled = false;
  onCleanup(() => {
    cancelled = true;
  });

  const gallery = productEnrichmentGallery.value;
  if (gallery.length === 0) {
    media.value = {};
    return;
  }

  void (async () => {
    const mediaItems = await Promise.all(
      gallery.map(async (item: IMedia) => {
        const mediaItemInterface = await useMedia(item);
        if (!mediaItemInterface) {
          return undefined;
        }

        return {
          [item.sys.id]: mediaItemInterface satisfies MediaItemInterface
        };
      })
    );

    if (cancelled) {
      return;
    }

    media.value = Object.assign({}, ...mediaItems.filter(isDefined));
  })();
});

const technicalDrawingItems = computed<GalleryItem[]>(() => {
  if (!store.decoratedProduct?.technicalDrawing.isValid()) {
    return [];
  }

  const src = store.decoratedProduct.technicalDrawing.getTechnicalDrawingLink();
  if (!src) {
    return [];
  }

  const altText = [
    $textDictionary.value['pdp.specs.technical_drawing.label'],
    store.decoratedProduct.name
  ]
    .filter(isDefined)
    .join(' ')
    .trim();

  const media = generateImageObject(
    {
      url: src,
      width: PDP_GALLERY_SECONDARY_THUMB_WIDTH,
      height: PDP_GALLERY_SECONDARY_THUMB_HEIGHT,
      altTag:
        altText ||
        store.decoratedProduct.technicalDrawing.getLabel() ||
        'Technical Drawing'
    },
    {
      width: PDP_GALLERY_SECONDARY_THUMB_WIDTH,
      height: PDP_GALLERY_SECONDARY_THUMB_HEIGHT
    }
  );

  return media ? [{ media }] : [];
});

const chartGalleryConfigs = computed(() => [
  {
    data: store.decoratedProduct?.emvaSpectralSensitivityData,
    descriptionKey: 'pdp.specs.emvaChart.label',
    descriptionFallback: 'EMVA Spectral Sensitivity',
    xTitleKey: 'pdp.specs.emvaChart.xTitleText',
    xTitleFallback: 'Wavelength λ [nm]',
    yTitleKey: 'pdp.specs.emvaChart.yTitleText',
    yTitleFallback: 'QuantumEfficiency %'
  },
  {
    data: store.decoratedProduct?.transmissionGraphData,
    descriptionKey: 'pdp.specs.transmissionGraph.label',
    descriptionFallback: 'Transmission Graph',
    xTitleKey: 'pdp.specs.transmissionGraph.xTitleText',
    xTitleFallback: 'Wavelength λ [nm]',
    yTitleKey: 'pdp.specs.transmissionGraph.yTitleText',
    yTitleFallback: 'Transmission %'
  }
]);

const chartThumbnailItem = computed<GalleryItem[]>(() =>
  chartGalleryConfigs.value
    .filter(
      (config) => isDefined(config.data) && (config.data?.length ?? 0) > 0
    )
    .map(() => ({
      media: buildFixedThumbnailMedia(fallbackChartImage?.url ?? undefined) ?? {
        type: 'image' as const,
        content: { src: '' }
      }
    }))
);

const chartLightboxItem = computed<GalleryItem[]>(() =>
  chartGalleryConfigs.value
    .filter(
      (config) => isDefined(config.data) && (config.data?.length ?? 0) > 0
    )
    .map((config) => ({
      media: {
        type: 'chart' as const,
        content: {
          chartData: [
            {
              label:
                store.decoratedProduct?.name ??
                store.decoratedProduct?.sku ??
                '',
              data: config.data as { x: number; y: number }[]
            }
          ],
          chartDescription:
            $textDictionary.value[config.descriptionKey] ??
            config.descriptionFallback,
          chartType: 'line' as const,
          xTitleText:
            $textDictionary.value[config.xTitleKey] ?? config.xTitleFallback,
          yTitleText:
            $textDictionary.value[config.yTitleKey] ?? config.yTitleFallback,
          yAxisMin: 0,
          yAxisMax: 100
        }
      }
    }))
);

const galleryItems = computed<GalleryItem[]>(() => {
  const items: GalleryItem[] = [
    ...productImagesThumbnails.value,
    ...technicalDrawingItems.value,
    ...chartThumbnailItem.value,
    ...productEnrichmentGalleryThumbnails.value
  ];

  return items.map((item: GalleryItem, index: number) => ({
    media: applyPdpGallerySlotSizes(item.media, index === 0)
  }));
});

const firstGalleryMedia = computed(() => galleryItems.value[0]?.media);

useProgressiveMediaOverlay(firstGalleryMedia, {
  root: galleryRoot,
  previewWidth: PDP_PREVIEW_WIDTH,
  previewQuality: PDP_PREVIEW_QUALITY,
  preloadHighResInHead: true,
  preloadKey: 'pdp-gallery-image'
});

const rawLightboxItems = computed<GalleryItem[]>(() => {
  return [
    ...productImages.value,
    ...technicalDrawingItems.value,
    ...chartLightboxItem.value,
    ...productEnrichmentGallery.value.map((element) => ({
      media: media.value[element.sys.id]
    }))
  ];
});

const lightboxGallery = computed<LightboxGalleryInterface | undefined>(() => {
  if (!store.decoratedProduct) {
    return undefined;
  }

  return {
    title: galleryTitle.value,
    isOpen: false,
    triggerBtnLabel: galleryTriggerLabel.value,
    nextBtnLabel: galleryNextLabel.value,
    prevBtnLabel: galleryPrevLabel.value,
    closeBtnLabel: galleryCloseLabel.value,
    mediaList: rawLightboxItems.value
  };
});
</script>

<style scoped>
:deep(.product-detail-gallery__item:first-child) {
  aspect-ratio: 4 / 3;
}
</style>
